import { useQuestions } from '../questions/QuestionsContext';
import type { QuestionFieldKey } from '../questions/questionFields';
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

interface BaseProps {
  label: string;
  required?: boolean;
  error?: string | undefined;
  hint?: string | undefined;
}

function Label({ id, label, required }: { id: string; label: string; required?: boolean | undefined }) {
  return (
    <label htmlFor={id} className="eo-field__label">
      {label}
      {required && (
        <span className="eo-field__req" aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </label>
  );
}

function Messages({ id, error, hint }: { id: string; error?: string | undefined; hint?: string | undefined }) {
  return (
    <>
      {hint && !error && (
        <p id={`${id}-hint`} className="eo-field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="eo-field__error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

const describedBy = (id: string, error?: string, hint?: string) => (error ? `${id}-error` : hint ? `${id}-hint` : undefined);

export function EoInput({ label, required, error, hint, ...rest }: BaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>) {
  const id = useId();
  return (
    <div className="eo-field">
      <Label id={id} label={label} required={required} />
      <input
        id={id}
        className="eo-input"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      />
      <Messages id={id} error={error} hint={hint} />
    </div>
  );
}

export function EoSelect({
  label,
  required,
  error,
  hint,
  children,
  ...rest
}: BaseProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & { children: ReactNode }) {
  const id = useId();
  return (
    <div className="eo-field">
      <Label id={id} label={label} required={required} />
      <select
        id={id}
        className="eo-input eo-select"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      >
        {children}
      </select>
      <Messages id={id} error={error} hint={hint} />
    </div>
  );
}

/**
 * A read-only value that looks like Figma's disabled field but is plain text (no input), so it cannot be edited or submitted.
 * With `askKey` (and inside the onboarding layout) it also offers "Ask about this", which opens the question box for that field.
 */
export function EoReadOnly({ label, value, placeholder = 'Not set yet', askKey }: {
  label: string; value: string | null | undefined; placeholder?: string; askKey?: QuestionFieldKey;
}) {
  const id = useId();
  const questions = useQuestions();
  const has = value !== null && value !== undefined && value !== '';
  const asked = askKey && questions ? questions.forField(askKey) : [];
  const answered = asked.some((q) => q.status === 'answered');
  return (
    <div className="eo-field">
      <span id={id} className="eo-field__label">
        {label}
      </span>
      <div className={`eo-readonly${has ? '' : ' eo-readonly--empty'}`} aria-labelledby={id} role="group">
        {has ? value : placeholder}
      </div>
      {askKey && questions && (
        <button type="button" className="eo-ask" onClick={() => questions.ask(askKey)} aria-label={`Ask about ${label}`}>
          Ask about this
          {answered && <span className="eo-ask__badge">Reply received</span>}
          {!answered && asked.length > 0 && <span className="eo-ask__badge eo-ask__badge--pending">Question sent</span>}
        </button>
      )}
    </div>
  );
}

/** YES / NO radios for a required question (the answer is a real boolean, never defaulted). */
export function EoYesNo({
  label,
  required,
  value,
  onChange,
  error,
  disabled,
}: {
  label: string;
  required?: boolean;
  value: boolean | null;
  onChange: (v: boolean) => void;
  error?: string | undefined;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <fieldset className="eo-field eo-radiogroup" aria-describedby={error ? `${id}-error` : undefined} disabled={disabled}>
      <legend className="eo-field__label">
        {label}
        {required && (
          <span className="eo-field__req" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </legend>
      <div className="eo-radiogroup__options">
        <label className="eo-radio">
          <input type="radio" name={id} checked={value === true} onChange={() => onChange(true)} aria-required={required || undefined} />
          <span>YES</span>
        </label>
        <label className="eo-radio">
          <input type="radio" name={id} checked={value === false} onChange={() => onChange(false)} aria-required={required || undefined} />
          <span>NO</span>
        </label>
      </div>
      {error && (
        <p id={`${id}-error`} className="eo-field__error" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** A switch. `disabled` renders the state read-only (for example the administrator-owned SUI override). */
export function EoToggle({
  label,
  checked,
  onChange,
  disabled,
  describedBy,
}: {
  label: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  describedBy?: string | undefined;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      className={`eo-toggle${checked ? ' eo-toggle--on' : ''}`}
      onClick={() => onChange?.(!checked)}
    >
      <span className="eo-toggle__knob" aria-hidden="true" />
    </button>
  );
}
